/**
 * FedRampBuildChat Component
 *
 * Handles the chat interface for the TAP Build Agent in FedRamp projects.
 * This agent combines messages from multiple agents: Forms, Workflow, Administration, Report, and Queue.
 */

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';


import {
  Icon,
  Text,
  Button,
  ChatMessage,
  ChatInput,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { ITokenUsage, IDevelopmentTiming } from '@ui-library/TokenUsageTooltip/TokenUsageTooltip.type';

import api, { authenticatedFetch, getApiOrigin } from '../../utils/axios';
import { formatTimestamp } from '../../utils/dateUtils';
import { DOCUMENT_EXTENSIONS } from '../../utils/fileUploadConfig';
import { useAuth } from '../../contexts/AuthContext';
import { tapBuildWebsocketManager, TapBuildWebSocketMessage } from '../../services/tapBuildWebsocketService';
import { IfHasPermission } from '../IfHasPermission';
import { ReadOnlyProjectNotice } from '../ReadOnlyProjectNotice';
import { Permissions } from '../../constants';
import { ChatNavigator, useChatIndex, useChatWindow } from '../ChatNavigator';
import { ChatLoadingIndicator } from '../chat/ChatLoadingIndicator';
import { useTicketTagChips } from '../../hooks/useTicketTagChips';
import { serializeTicketTagsIntoContent } from '../../utils/cabTagSerializer';
import { usePromptQueue } from '../../hooks/usePromptQueue';
import { TAP_QUEUE_BASE } from '../../api/queueApi';
import { QueuedMessageBanner } from '../chat/QueuedMessageBanner';

import type { Attachment } from '../../types/chat';
import type { TapAgentMessage, FedRampBuildChatProps } from './FedRampBuildChat.types';

// DOM contract shared with the conversation navigator: each rendered message
// is wrapped in an element with id="message-<id>"
const getMessageElement = (id: string) => document.getElementById(`message-${id}`);

/**
 * The single rule for whether a run's assistant bubble exists.
 *
 * Web guarantees one thinking row by giving it a fixed id ('agent-thinking')
 * that only updateThinkingMessage creates. Low-code can't use a literal fixed id
 * — the bubble becomes the real assistant message, so it has to carry the run's
 * own id — but it takes the same approach: decide by identity, never by
 * position. The old guard asked "is the LAST message an empty assistant?", which
 * is only right while the bubble happens to sit at the tail; anything appended
 * after it produced a second bubble.
 *
 * The run's bubble appears under one of two ids: the real one in the window that
 * streamed it (stamped by stream_start), the `streaming-` prefixed one
 * everywhere else.
 */
const ensureAssistantBubble = (
  prev: TapAgentMessage[],
  assistantMessageId?: string | null
): TapAgentMessage[] => {
  if (!assistantMessageId) return prev;

  // Nothing to add before history has loaded: the initial fetch replaces the
  // list outright, so a bubble created now is wiped and never recreated.
  if (prev.length === 0) return prev;

  if (prev.some(m => m.id === assistantMessageId || m.id === `streaming-${assistantMessageId}`)) {
    return prev;
  }

  // The window that sent this run already has an optimistic empty bubble at the
  // tail. Adopt it by renaming to the real id rather than adding a second one;
  // stream_start's own rename then matches nothing and harmlessly does nothing.
  const last = prev[prev.length - 1];
  if (last && last.role === 'assistant' && !last.content && last.id.startsWith('assistant-')) {
    return prev.map(m => (m === last ? { ...m, id: assistantMessageId } : m));
  }

  return [
    ...prev,
    {
      id: `streaming-${assistantMessageId}`,
      role: 'assistant',
      content: '',
      created_at: Date.now() / 1000,
    },
  ];
};

/** Index of the last message matching a predicate, or -1. */
const findLastIndex = (
  list: TapAgentMessage[],
  predicate: (m: TapAgentMessage) => boolean
): number => {
  for (let i = list.length - 1; i >= 0; i--) {
    if (predicate(list[i])) return i;
  }
  return -1;
};

// Map TapAgentMessage token_usage to ITokenUsage format
const mapTapAgentTokenUsage = (message: TapAgentMessage): ITokenUsage | undefined => {
  if (!message.token_usage) return undefined;

  return {
    inputTokens: message.token_usage.input_tokens,
    outputTokens: message.token_usage.output_tokens,
    cacheCreationInputTokens: 0,
    cacheReadInputTokens: 0,
    costs: message.token_usage.costs ? {
      input_cost: message.token_usage.costs.input_cost,
      output_cost: message.token_usage.costs.output_cost,
      cache_write_cost: 0,
      cache_read_cost: 0,
      total_cost: message.token_usage.costs.total_cost,
    } : undefined
  };
};

// Map TapAgentMessage timing to IDevelopmentTiming format
const mapTapAgentTiming = (message: TapAgentMessage): IDevelopmentTiming | undefined => {
  if (!message.duration_seconds) return undefined;

  return {
    duration_seconds: message.duration_seconds || 0,
  };
};

export const FedRampBuildChat: React.FC<FedRampBuildChatProps> = ({
  project,
  projectId,
  onLoadingChange,
  onCostUpdate,
  onAgentTypeDetected,
  onTicketTagClick,
  onEpicTagClick
}) => {
  const [messages, setMessages] = useState<TapAgentMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [abortController, setAbortController] = useState<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false);
  const isLinked = project?.tap_url !== null;

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Downward pagination (loading newer messages below a historical window —
  // e.g. after a conversation-navigator jump into the middle of the chat)
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [isLoadingNewer, setIsLoadingNewer] = useState(false);

  // Live mirror of `messages`. WebSocket handlers need to inspect the current
  // list synchronously — a setMessages updater only runs at render time, which
  // is too late to decide whether a fallback reload is needed.
  const messagesRef = useRef<TapAgentMessage[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  /**
   * Write an agent progress line ("Creating form…") onto the bubble it belongs
   * to, mirroring web's updateThinkingMessage.
   *
   * The text deliberately lives on the message rather than in its own state:
   * a scalar would be a dependency other hooks can take, and a value that
   * changes on every progress line then rebuilds those hooks. That is exactly
   * how checkStreamingStatus came to fire an HTTP request per progress line and
   * force-reload history at the moment the queue advanced. Empty deps here, so
   * this function's identity never changes.
   */
  const applyThinkingAction = useCallback(
    (assistantMessageId: string | undefined, text: string) => {
      if (!text) return;
      setMessages(prev => {
        // Same two id shapes as the completion handler: the real id in the
        // window that streamed it, the prefixed one everywhere else.
        let targetId = assistantMessageId
          ? prev.find(
              m => m.id === assistantMessageId || m.id === `streaming-${assistantMessageId}`
            )?.id
          : undefined;

        if (!targetId) {
          // No id to match on — the streaming window before stream_start has
          // stamped its ids. The trailing empty assistant bubble is the one
          // being generated.
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && !last.content) targetId = last.id;
        }
        if (!targetId) return prev;

        return prev.map(m => (m.id === targetId ? { ...m, thinkingMessage: text } : m));
      });
    },
    []
  );

  // Re-entrancy lock for the three pagination flows (loadMore, jumpToMessage,
  // loadNewer) so two page fetches can't interleave and corrupt the scroll
  // restore. Deliberately NOT a mirror of isLoading, which is what it used to
  // double as: that made every pagination guard true for the whole duration of
  // a prompt — blocking scrollback entirely while the agent worked — and let an
  // unrelated effect release the lock mid-fetch.
  //
  // Separate from isLoadingMessagesRef on purpose: loadMore calls
  // fetchMessageHistory, which takes that one, so sharing it would deadlock.
  const isPaginatingRef = useRef(false);

  // Ref to prevent multiple simultaneous message loads
  const isLoadingMessagesRef = useRef(false);
  const pendingRefreshRef = useRef(false);

  // True once this window has seen a run reported as active, so the moment the
  // status flips to "nothing running" we know to pick up the result. Without it
  // a quiet page load would refetch history for no reason.
  const sawActiveRunRef = useRef(false);

  // Polling fallback refs
  const isPollingRef = useRef(false);
  const pollingTimeoutRef = useRef<number | null>(null);

  // ── Prompt queue ──────────────────────────────────────────────────────────
  // job_id of the queued prompt currently loaded into the input for editing.
  const [editingQueueJobId, setEditingQueueJobId] = useState<string | null>(null);

  // Get user for ChatMessage component (token feeds the ticket tag search)
  const { user, token } = useAuth();

  // ── Ticket / epic @-references ──────────────────────────────────────────────
  // A tagged message is routed to the implementation worker, which builds every
  // tagged ticket (and every ticket of a tagged epic) in one job. It still takes
  // no file uploads, so that one send the backend would refuse is vetoed before
  // the input clears (see handleSendMessage).
  const {
    selectedTicketTags,
    setSelectedTicketTags,
    ticketTagSuggestions,
    isTicketTagSearching,
    handleTicketTagSearch,
    handleTicketTagsChange,
  } = useTicketTagChips(projectId || undefined, token);

  const canEditProject = !!project?.permissions?.includes(Permissions.UpdateProjectPermission);

  // Serial execution per project: while one prompt runs, the next is queued
  // rather than started alongside it. TAP_QUEUE_BASE points the shared hook at
  // the low-code queue routes.
  // The WebSocket handler calls promptQueue.refresh() directly, so no message is
  // threaded through the hook: doing both meant every queue event fetched the
  // queue twice. ChatPage still uses that channel — its handler is defined
  // before the hook runs, so it cannot reach refresh any other way.
  const promptQueue = usePromptQueue(projectId || '', null, TAP_QUEUE_BASE);

  // Load a queued prompt into the input when the user clicks Edit on its card,
  // and clear the input if that prompt leaves the queue while being edited
  // (someone else cancelled it, or it started running).
  useEffect(() => {
    if (promptQueue.editingJob && editingQueueJobId !== promptQueue.editingJob.job_id) {
      setInput(promptQueue.editingJob.user_message || '');
      setEditingQueueJobId(promptQueue.editingJob.job_id);
    } else if (!promptQueue.editingJob && editingQueueJobId) {
      setEditingQueueJobId(null);
      setInput('');
      // Otherwise the edited prompt's @ticket tags stay selected and are
      // serialised into the next message. Save and Cancel already clear them;
      // this is the exit where the prompt ran before the edit was saved.
      setSelectedTicketTags([]);
    }
  }, [promptQueue.editingJob, editingQueueJobId]);

  // Check streaming status on mount
  const checkStreamingStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      console.log('🔍 [TapBuildChat] Checking streaming status on mount/reconnect');
      const response = await authenticatedFetch(
        `/api/projects/${projectId}/tap-agent/build/streaming-status`
      );
      const data = await response.json();

      console.log('📊 [TapBuildChat] Streaming status:', data);

      if (data.is_streaming && data.status === 'active') {
        // Same identity rule as the two WebSocket paths. The old code here was a
        // third variant that marked whatever assistant message happened to be
        // last as "thinking" — including a finished answer from a previous run.
        // No marking is needed: the render derives the thinking state from
        // isLoading plus an empty trailing assistant bubble.
        sawActiveRunRef.current = true;
        setMessages(prev => ensureAssistantBubble(prev, data.assistant_message_id));

        setIsLoading(true);

        // Poll again after 5 seconds (optimized for performance)
        if (isPollingRef.current) {
          pollingTimeoutRef.current = window.setTimeout(() => checkStreamingStatus(), 5000);
        }

      } else {
        // Nothing is running. Whatever the last run did — finished, cancelled,
        // errored, or ended before this page was even opened — its outcome is
        // already written into the assistant message, so history is the only
        // source. Two branches, exactly like web's check, which has no error
        // branch at all.
        //
        // There used to be one. It built a chat row out of the streaming state's
        // error_message: a row that existed only in the browser, carried an
        // internal diagnostic string ("Interrupted by server restart") instead of
        // the sentence written for the user, and used a Date.now() id so nothing
        // recognised it as a repeat. checkStreamingStatus runs twice on page
        // load, so an interrupted run showed its real message plus two invented
        // copies — and again after every reload, since they were never saved.
        console.log('📡 [TapBuildChat] checkStreamingStatus: nothing running, status=', data.status);
        setIsLoading(false);
        isPollingRef.current = false; // Stop polling

        // Only reload when this window was actually showing a run: that is the
        // socket-down case, where the poll is how we learn it finished. On a
        // quiet page load there is nothing to pick up and history was just
        // fetched anyway.
        if (sawActiveRunRef.current) {
          sawActiveRunRef.current = false;
          fetchMessageHistory();
        }
      }
    } catch (error) {
      console.error('❌ [TapBuildChat] Error checking streaming status:', error);
      setIsLoading(false);
      isPollingRef.current = false; // Stop polling on error
    }
    // Deps stay stable on purpose. This used to include thinkingAction, so the
    // callback was rebuilt on every progress line — and the effect below, which
    // depends on its identity, re-fired a status request each time. See
    // applyThinkingAction.
  }, [projectId]);

  // WebSocket message handler
  const handleTapBuildWebSocketMessage = useCallback((message: TapBuildWebSocketMessage) => {
    console.log('📨 [TapBuildChat] WebSocket message:', message.type, message);

    if (
      message.type === 'queue_position_update' ||
      message.type === 'queue_job_started' ||
      message.type === 'queue_job_completed'
    ) {
      // refresh's identity is stable (useCallback on projectId + base, both
      // stable), so closing over it here can never go stale.
      promptQueue.refresh();
    }

    if (message.type === 'queue_job_started') {
      // The next queued prompt is running now. Say so immediately rather than
      // waiting for tap_build_streaming_started, which lands a few database
      // writes later — otherwise the input briefly offers Send instead of Queue.
      // Deliberately nothing else: the user message and the thinking bubble both
      // arrive on their own events, and adding them here would duplicate them.
      setIsLoading(true);
    }

    switch (message.type) {
      case 'tap_build_action': {
        // Progress line mirrored off the SSE. Windows that hold the SSE also
        // receive it there and write the same text — patching twice is harmless.
        applyThinkingAction(message.data.assistant_message_id, message.data.message || '');
        break;
      }

      case 'tap_build_user_message_added': {
        const incomingId = message.data.message_id;
        const incomingContent = message.data.content || '';

        setMessages(prev => {
          // ── 1. Already present, by id ──────────────────────────────────
          // Makes the handler idempotent: a replayed event, a second window's
          // copy, or one arriving after a reload already fetched the message
          // all become no-ops. Without this the only guard was "does the LAST
          // user row have the same text", which misses whenever the matching
          // row isn't last — and appends a duplicate.
          if (incomingId && prev.some(m => m.id === incomingId)) {
            console.log('🔄 [TapBuildChat] User message already present, skipping');
            return prev;
          }

          // ── 2. This window sent it and is showing an optimistic row ─────
          // Replace it so the row picks up its real database id. The content
          // check is gated on the id still being optimistic — comparing text
          // unconditionally silently swallowed a prompt that repeated the
          // previous one word for word.
          const lastUserIndex = findLastIndex(prev, m => m.role === 'user');
          const lastUser = lastUserIndex >= 0 ? prev[lastUserIndex] : undefined;

          if (lastUser && lastUser.id.startsWith('user-') && lastUser.content === incomingContent) {
            console.log('✅ [TapBuildChat] Replacing optimistic user message with canonical one');
            const updated = [...prev];
            updated[lastUserIndex] = {
              ...lastUser,
              id: incomingId || lastUser.id,
              user_info: message.data.user_info || lastUser.user_info,
              // Prefer what the server resolved, but never blank the chips the
              // user is already looking at.
              attachments: message.data.attachments?.length
                ? message.data.attachments
                : lastUser.attachments,
            };
            return updated;
          }

          // ── 3. Genuinely new (the dequeued-prompt path) ────────────────
          const newUserMessage: TapAgentMessage = {
            id: incomingId || Date.now().toString(),
            role: 'user',
            content: incomingContent,
            created_at: Date.now() / 1000,
            user_info: message.data.user_info,
            attachments: message.data.attachments?.length ? message.data.attachments : undefined
          };

          console.log('✅ [TapBuildChat] Adding user message from WebSocket');

          // Insert above a trailing empty assistant bubble rather than after it.
          // Web can append blindly because its thinking row is transient and gets
          // filtered out at completion; here the bubble IS the assistant message
          // and stays, so if it was created first a blind append would leave the
          // user's prompt rendered underneath the thinking indicator.
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            return [...prev.slice(0, -1), newUserMessage, last];
          }
          return [...prev, newUserMessage];
        });
        break;
      }

      case 'tap_build_streaming_started': {
        // Streaming started (possibly from another window)
        console.log('🔄 [TapBuildChat] Streaming started');

        // Deliberately unconditional. When the queue advances, the previous
        // prompt's completed event and this one arrive back to back, so any
        // guard reading a state mirror here would see a stale value, swallow the
        // next prompt's start, and leave the UI looking idle while it runs.
        // Duplicate bubbles are prevented by identity in ensureAssistantBubble.
        console.log('📡 [TapBuildChat] Setting isLoading=true');
        setIsLoading(true);

        // One rule decides whether this run's bubble exists — see
        // ensureAssistantBubble. The old positional guard here was one of three
        // different guards across three creation paths.
        setMessages(prev => ensureAssistantBubble(prev, message.data.assistant_message_id));
        break;
      }

      case 'tap_build_streaming_completed': {
        console.log('✅ [TapBuildChat] Streaming completed');
        setIsLoading(false);
        isPollingRef.current = false;

        // Patch the finished message in place, never refetch. A refetch here is
        // a full list replace against a snapshot the server took before the GET
        // left; with a queue the next prompt starts inside that window, so its
        // user message — already appended from the WebSocket — gets wiped and
        // the next thinking bubble appears above nothing. Web's completion
        // handler patches for the same reason and never reloads.
        //
        // The bubble carries one of two ids depending on how this window learned
        // about the run:
        //   <realId>              the window that streamed it, stamped by stream_start
        //   streaming-<realId>    every other window, built from tap_build_streaming_started
        // A dequeued prompt has no SSE anywhere, so every window — including the
        // one that queued it — is the second case.
        const completionData = message.data;
        const realId = completionData.assistant_message_id;
        const bubbleId = realId
          ? messagesRef.current.find(
              m => m.id === realId || m.id === `streaming-${realId}`
            )?.id
          : undefined;

        if (realId && bubbleId) {
          setMessages(prev =>
            prev.map(msg => {
              if (msg.id !== bubbleId) return msg;
              return {
                ...msg,
                // Rename to the real DB id so the conversation navigator and any
                // later jump can address this message.
                id: realId,
                // Keep text already on screen — the payload is only a fallback
                // for a window that never streamed it.
                content: msg.content || completionData.content || '',
                token_usage:
                  completionData.token_usage && Object.keys(completionData.token_usage).length > 0
                    ? (completionData.token_usage as TapAgentMessage['token_usage'])
                    : msg.token_usage,
                duration_seconds: completionData.duration_seconds ?? msg.duration_seconds,
                isThinking: false,
                isStreaming: false,
              };
            })
          );
        } else {
          // Nothing matched: this window never saw the run start (socket opened
          // late, or stream_start never arrived so the bubble still carries its
          // local id). A reload is the only way to get the message; the race
          // above applies, which is why fetchMessageHistory keeps anything newer
          // than the snapshot it receives.
          console.log(
            `ℹ️ [TapBuildChat] No bubble matched ${realId} — reloading history`
          );
          fetchMessageHistory();
        }

        // Refresh project cost
        if (projectId && onCostUpdate) {
          api.get(`/projects/${projectId}/stats`).then(response => {
            const totalCost = parseFloat(response.data.total_cost) || 0;
            onCostUpdate(totalCost);
          }).catch(err => {
            console.error('Failed to fetch project cost:', err);
          });
        }
        break;
      }

      case 'tap_build_streaming_cancelled':
        console.log('⚠️ [TapBuildChat] Streaming cancelled');
        setIsLoading(false);
        isPollingRef.current = false;

        // Reload messages to get the cancelled message from DB
        fetchMessageHistory();
        break;

      case 'tap_build_streaming_error':
        console.log('❌ [TapBuildChat] Streaming error');
        setIsLoading(false);
        isPollingRef.current = false;
        
        // Only auto-scroll if user is already at bottom
        if (!hasUserScrolledUp) {
          setHasUserScrolledUp(false);
        }
        
        // Reload messages to get the error message from DB
        fetchMessageHistory();
        break;

      case 'tap_build_streaming_status': {
        // Initial streaming status on connect (received when passive window connects)
        console.log('📡 [TapBuildChat] Received streaming_status:', message.data);
        if (message.data.is_streaming && message.data.status === 'active') {
          console.log('📡 [TapBuildChat] Active streaming detected - showing loader');
          setIsLoading(true);

          setMessages(prev => ensureAssistantBubble(prev, message.data.assistant_message_id));
        }
        break;
      }
    }
  // promptQueue.refresh rather than promptQueue: the object is rebuilt every
  // render, the function is not, so this declares what is used without
  // rebuilding the handler on every pass.
  }, [projectId, isLoading, onCostUpdate, hasUserScrolledUp, promptQueue.refresh]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      isPollingRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [projectId]);

  // Connect to WebSocket
  useEffect(() => {
    if (!projectId) return;

    const token = localStorage.getItem('token') || '';

    console.log('🔌 [TapBuildChat] Connecting to WebSocket for project:', projectId);

    tapBuildWebsocketManager.connect(projectId, token, {
      onMessage: handleTapBuildWebSocketMessage,
      onConnect: () => {
        console.log('✅ [TapBuildChat] WebSocket connected');
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        checkStreamingStatus();
      },
      onReconnect: () => {
        console.log('🔄 [TapBuildChat] WebSocket reconnected, reloading messages');
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        checkStreamingStatus();
        fetchMessageHistory();
      },
      onDisconnect: () => {
        console.log('⚠️ [TapBuildChat] WebSocket disconnected');
        if (isLoading) {
          isPollingRef.current = true;
          checkStreamingStatus();
        }
      },
      onError: (error) => {
        console.error('📡 [TapBuildChat] WebSocket Error:', error);
        if (isLoading) {
          isPollingRef.current = true;
          checkStreamingStatus();
        }
      }
    });

    return () => {
      // Don't disconnect here - let the hook manage its own lifecycle
    };
  }, [projectId, handleTapBuildWebSocketMessage, checkStreamingStatus, isLoading]);

  // Check streaming status on mount
  useEffect(() => {
    if (projectId) {
      checkStreamingStatus();
    }
    // Intentionally keyed on projectId alone — depending on the callback's
    // identity is what turned this one-shot mount check into a per-progress-line
    // poller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    fetchMessageHistory(true);
  }, [projectId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load more messages (pagination)
  const loadMore = useCallback(async (container: HTMLDivElement) => {
    if (isPaginatingRef.current || isLoadingMore) return;
    if (!hasMoreMessages || !projectId) return;

    // Get the oldest message ID before starting the load
    const oldestMessage = messages[0];
    if (!oldestMessage || !oldestMessage.id) {
      console.warn('⚠️ No oldest message found for pagination');
      return;
    }

    isPaginatingRef.current = true;
    setIsLoadingMore(true);

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      // Call fetchMessageHistory directly with the oldest message ID
      await fetchMessageHistory(false, 20, oldestMessage.id);
    } finally {
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        isPaginatingRef.current = false;
        setIsLoadingMore(false);
      });
    }
  }, [hasMoreMessages, projectId, isLoadingMore, messages]); // fetchMessageHistory is defined below, so we can't include it here

  // Handle scroll to detect if user is at bottom. Pagination triggers live in
  // the chat-window hook (composed onto onScroll in the JSX) so navigator jumps
  // never kick off a page load mid-jump.
  const handleScroll = useCallback(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 100;

    const atBottom = distanceFromBottom < threshold;
    setIsAtBottom(atBottom);

    // If user scrolled up manually (away from bottom), set the flag
    if (!atBottom && !hasUserScrolledUp) {
      setHasUserScrolledUp(true);
    }

    // If user scrolled back to bottom manually, re-enable auto-scroll
    if (atBottom && hasUserScrolledUp) {
      setHasUserScrolledUp(false);
    }
  }, [hasUserScrolledUp]);

  // Auto-scroll only if user hasn't manually scrolled up AND we're at the true
  // tail — never yank a historical window (navigator jump) back to the bottom.
  useEffect(() => {
    if (!hasUserScrolledUp && !hasMoreNewer) {
      scrollToBottom();
    }
  }, [messages, hasUserScrolledUp, hasMoreNewer]);

  const fetchMessageHistory = async (isInitialLoad = false, limit = 20, beforeMessageId?: string) => {
    if (isLoadingMessagesRef.current) {
      pendingRefreshRef.current = true;
      return;
    }

    isLoadingMessagesRef.current = true;
    pendingRefreshRef.current = false;

    try {
      if (isInitialLoad) {
        setLoadingHistory(true);
        onLoadingChange?.(true);
      }

      // Build query parameters for pagination
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (beforeMessageId) params.append('before_message_id', beforeMessageId);

      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages = response.data.messages || [];

      // Update hasMore state based on pagination metadata
      setHasMoreMessages(response.data?.pagination?.has_more || false);

      // Handle pagination: prepend vs replace
      if (beforeMessageId) {
        setMessages(prevMessages => [...newMessages, ...prevMessages]);
      } else {
        // Replacing with the latest page — this is the true tail, so nothing
        // newer remains below (clears any historical-window state).
        setHasMoreNewer(false);
        // Just set messages - checkStreamingStatus will handle marking empty messages as thinking.
        // Stagger the entrance animation only on the initial history load; messages
        // added later must animate immediately (see TapAgentMessage.staggerIndex).
        const page: TapAgentMessage[] = isInitialLoad
          ? newMessages.map((msg: TapAgentMessage, i: number) => ({ ...msg, staggerIndex: i }))
          : newMessages;

        setMessages(prev => {
          // The server snapshots this page before the request leaves, so any
          // message that arrives over the WebSocket while it is in flight is
          // missing from it. Replacing outright deletes that message — and with
          // a queue the next prompt starts milliseconds after the previous one
          // ends, so what gets deleted is usually the next prompt's user
          // message, leaving its thinking bubble sitting above nothing.
          //
          // Carry across whatever sits after the newest entry the page knows
          // about: by construction those were appended after the snapshot.
          // Position is used rather than created_at because messages appended
          // from a WebSocket event are stamped with the client's clock, which
          // isn't comparable to the server's.
          if (isInitialLoad || prev.length === 0 || page.length === 0) return page;

          const pageIds = new Set(page.map(m => m.id));

          let lastKnown = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (pageIds.has(prev[i].id)) {
              lastKnown = i;
              break;
            }
          }
          // No overlap at all — a historical window, or a different thread of
          // messages entirely. Nothing can be judged newer, so just take the page.
          if (lastKnown === -1) return page;

          const trailing = prev.slice(lastKnown + 1).filter(
            // Drop a local thinking placeholder whose real message is in the
            // page (the cancelled/error reloads hit exactly this), otherwise it
            // would linger as an empty bubble below the finished message.
            m => !(m.id.startsWith('streaming-') && pageIds.has(m.id.slice('streaming-'.length)))
          );
          return trailing.length ? [...page, ...trailing] : page;
        });
      }
    } catch (error) {
      console.error('Failed to fetch TAP build agent history:', error);
    } finally {
      setLoadingHistory(false);
      onLoadingChange?.(false);
      isLoadingMessagesRef.current = false;

      if (pendingRefreshRef.current) {
        pendingRefreshRef.current = false;
        fetchMessageHistory();
      }
    }
  };

  // ── Conversation navigator ────────────────────────────────────────────────
  // Lightweight full-thread index of user turns (id / timestamp / preview).
  const fetchNavIndex = useMemo(() => {
    if (!projectId) return null;
    return async () =>
      (await api.get(`/projects/${projectId}/tap-agent/build/chat-index`)).data.items;
  }, [projectId]);
  const { items: navItems, refresh: refreshNavIndex } = useChatIndex(fetchNavIndex);

  // Re-sync the index when a streaming turn completes (the user turn is
  // persisted with its real DB id by then).
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    if (prevLoadingRef.current && !isLoading) {
      refreshNavIndex();
    }
    prevLoadingRef.current = isLoading;
  }, [isLoading, refreshNavIndex]);

  // Replace the loaded list with a window starting at the target (inclusive).
  const jumpToMessage = useCallback(async (id: string) => {
    if (isPaginatingRef.current || !projectId) return null;
    isPaginatingRef.current = true;
    setIsLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('after_message_id', id);
      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages: TapAgentMessage[] = response.data.messages || [];
      setHasMoreMessages(response.data?.pagination?.has_more_older || false);
      setHasMoreNewer(response.data?.pagination?.has_more_newer || false);
      setMessages(newMessages);
      return { found: newMessages.some(m => m.id === id) };
    } catch (error) {
      console.error('Failed to jump to message:', error);
      return null;
    } finally {
      isPaginatingRef.current = false;
      setIsLoadingMore(false);
    }
  }, [projectId]);

  // Append the next newer page below a historical window.
  const loadNewer = useCallback(async () => {
    if (isPaginatingRef.current || !projectId || !hasMoreNewer) return;
    const newest = messages[messages.length - 1];
    if (!newest?.id) return;
    isPaginatingRef.current = true;
    setIsLoadingNewer(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', '20');
      params.append('after_message_id', newest.id);
      const response = await api.get(`/projects/${projectId}/tap-agent/build/messages?${params.toString()}`);
      const newMessages: TapAgentMessage[] = response.data.messages || [];
      setHasMoreNewer(response.data?.pagination?.has_more_newer || false);
      // after_message_id is inclusive of the ref — dedup by id when appending.
      setMessages(prev => {
        const existing = new Set(prev.map(m => m.id));
        return [...prev, ...newMessages.filter(m => !existing.has(m.id))];
      });
    } catch (error) {
      console.error('Failed to load newer messages:', error);
    } finally {
      isPaginatingRef.current = false;
      setIsLoadingNewer(false);
    }
  }, [projectId, hasMoreNewer, messages]);

  // useChatWindow needs a `timestamp` field; map created_at (epoch seconds) to
  // a Date-parseable millisecond value. Reused for the rail's loaded-window props.
  const windowMessages = useMemo(
    () => messages.map(m => ({ ...m, timestamp: (m.created_at ?? 0) * 1000 })),
    [messages],
  );

  const navAdapter = useMemo(() => ({
    jumpToMessage,
    loadOlder: (container: HTMLDivElement) => {
      if (hasMoreMessages && !isLoadingMore && !isPaginatingRef.current) {
        loadMore(container);
      }
    },
    loadNewer: () => {
      if (hasMoreNewer && !isLoadingNewer && !isPaginatingRef.current) {
        loadNewer();
      }
    },
  }), [jumpToMessage, hasMoreMessages, isLoadingMore, loadMore, hasMoreNewer, isLoadingNewer, loadNewer]);

  const chatWindow = useChatWindow({
    containerRef: chatContainerRef,
    messages: windowMessages,
    getMessageElement,
    adapter: navAdapter,
    // A jump target that can't be loaded means the index is stale (message gone)
    onStaleTarget: refreshNavIndex,
  });
  // ──────────────────────────────────────────────────────────────────────────

  const handleStopStreaming = async () => {
    console.log('🛑 Stopping TAP build agent conversation...');

    if (abortController) {
      abortController.abort();
      setAbortController(null);
    }

    if (projectId) {
      try {
        await api.post(`/projects/${projectId}/tap-agent/build/stop`);

        console.log('📥 Reloading TAP build messages after stop...');
        await fetchMessageHistory();
      } catch (error) {
        console.error('❌ Failed to stop TAP build stream:', error);
      }
    }

    setIsLoading(false);
    console.log('✅ TAP build agent conversation stopped');
  };

  /**
   * ChatInput onSend handler. Serializes the @-references and sends. Attachments
   * ride along with a tagged message too — the implementation endpoint takes
   * uploads, so there is nothing left to veto here.
   */
  const handleSendMessage = (messageContent?: string, attachments?: Attachment[]): boolean | void => {
    const rawMessage = (messageContent || input).trim();
    if (!rawMessage || isLoading) return;

    const ticketTags = selectedTicketTags;
    // Serialize each @-reference into a {{ticket:TKT-001|label}} / {{epic:id|label}}
    // token: the backend routes on them and the message renderer shows them as chips.
    const userMessage = ticketTags.length
      ? serializeTicketTagsIntoContent(rawMessage, ticketTags)
      : rawMessage;
    if (ticketTags.length) setSelectedTicketTags([]);

    void performSendMessage(userMessage, attachments);
  };

  /** Shape a just-queued prompt into a banner card, so it appears without
   *  waiting for the server round-trip that confirms it. */
  const buildOptimisticJob = (
    payload: { job_id?: string; position?: number },
    userMessage: string,
    attachments?: Attachment[]
  ) => ({
    job_id: payload.job_id || `optimistic-${Date.now()}`,
    status: 'QUEUED' as const,
    position: payload.position,
    user_message: userMessage,
    attachments: attachments || [],
    created_at: new Date().toISOString(),
    user_info: user
      ? {
          id: user.id,
          email: user.email,
          username: user.username,
          firstName: user.firstName,
          lastName: user.lastName,
        }
      : undefined,
  });

  /**
   * ChatInput onQueue handler — the user added a prompt while one is running.
   * No optimistic chat bubbles here: the prompt belongs in the queue banner
   * until it starts, and its real messages arrive over the WebSocket then.
   */
  const handleQueueMessage = async (msgText: string, attachments?: Attachment[]) => {
    if (!projectId) return;

    const ticketTags = selectedTicketTags;
    const outgoing = ticketTags.length
      ? serializeTicketTagsIntoContent(msgText, ticketTags)
      : msgText;

    try {
      const response = await api.post(`/projects/${projectId}/tap-agent/build/chat`, {
        query: outgoing,
        attachments: attachments || [],
      });

      // The prompt was accepted either way — clear the input. It normally comes
      // back queued, but the running prompt may have finished between the render
      // that showed the Queue button and this click, in which case the backend
      // started it straight away and answered with a stream this call can't read.
      // That prompt still runs; it just arrives over the WebSocket like a
      // dequeued one does, so only the token-by-token view is lost.
      setInput('');
      if (ticketTags.length) setSelectedTicketTags([]);

      if (response.data?.status === 'queued') {
        promptQueue.addOptimisticJob(
          buildOptimisticJob(response.data, outgoing, attachments) as any
        );
      }
    } catch (error: any) {
      // The text stays in the input so the user can retry without retyping.
      console.error('[TapBuildChat] Failed to queue message:', error);
    }
  };

  /** Edit on a queue card: load that prompt back into the input. */
  const handleEditJob = async (jobId: string) => {
    if (editingQueueJobId && editingQueueJobId !== jobId) {
      await promptQueue.cancelEdit(editingQueueJobId);
    }
    await promptQueue.startEditing(jobId);
  };

  const handleSaveEdit = async (msgText: string, attachments?: Attachment[]) => {
    if (!editingQueueJobId) return;
    const outgoing = selectedTicketTags.length
      ? serializeTicketTagsIntoContent(msgText, selectedTicketTags)
      : msgText;
    await promptQueue.saveEdit(
      editingQueueJobId,
      outgoing,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      attachments || []
    );
    setEditingQueueJobId(null);
    setInput('');
    setSelectedTicketTags([]);
  };

  const handleCancelEdit = async () => {
    if (editingQueueJobId) await promptQueue.cancelEdit(editingQueueJobId);
    setEditingQueueJobId(null);
    setInput('');
    setSelectedTicketTags([]);
  };

  const performSendMessage = async (userMessage: string, attachments?: Attachment[]) => {
    setInput('');
    setIsLoading(true);

    // Always scroll to bottom when user sends a new message
    setHasUserScrolledUp(false);

    const controller = new AbortController();
    setAbortController(controller);

    // Generate unique IDs for this conversation turn. These are temporary:
    // the stream's first event (stream_start) carries the real DB ids, which
    // get stamped onto the messages while they're still in thinking state.
    const userMessageId = `user-${Date.now()}`;
    const assistantMessageId = `assistant-${Date.now()}`;
    let currentUserId = userMessageId;
    let currentAssistantId = assistantMessageId;

    // Add user message to UI immediately (optimistic update)
    const optimisticUserMessage: TapAgentMessage = {
      id: userMessageId,
      role: 'user',
      content: userMessage,
      created_at: Date.now() / 1000,
      attachments: attachments && attachments.length > 0 ? attachments : undefined,
    };
    setMessages(prev => [...prev, optimisticUserMessage]);

    // Add thinking message for assistant
    const thinkingMessage: TapAgentMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      created_at: Date.now() / 1000,
    };
    setMessages(prev => [...prev, thinkingMessage]);

    // Variable to store detected agent type
    let detectedAgentType: string | null = null;

    try {
      const baseURL = getApiOrigin();

      const response = await authenticatedFetch(
        `${baseURL}/api/projects/${projectId}/tap-agent/build/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: userMessage,
            attachments: attachments || [],
          }),
          signal: controller.signal
        }
      );

      if (!response.ok) {
        // Surface the backend's error detail (e.g. unsupported attachment type)
        let errorDetail = '';
        try {
          const errorBody = await response.json();
          errorDetail = errorBody?.detail || '';
        } catch {
          // Non-JSON error body — fall back to the generic message
        }
        throw new Error(errorDetail || `HTTP error! status: ${response.status}`);
      }

      // The project turned out to be busy, so the backend queued this prompt and
      // answered with JSON instead of a stream. Take back the optimistic bubbles
      // — nothing is running for them — and show the prompt as a queue card.
      if ((response.headers.get('content-type') || '').includes('application/json')) {
        const queuedPayload = await response.json();
        if (queuedPayload?.status === 'queued') {
          setMessages(prev =>
            prev.filter(msg => msg.id !== currentUserId && msg.id !== currentAssistantId)
          );
          promptQueue.addOptimisticJob(
            buildOptimisticJob(queuedPayload, userMessage, attachments) as any
          );
          // isLoading deliberately stays true: another prompt is still running,
          // so the input must remain in queue mode.
          setAbortController(null);
          return;
        }
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('Response body is null');
      }

      let buffer = '';
      let assistantContent = '';
      let firstChunkReceived = false;

      console.log('🚀 Starting TAP Build Agent request stream...');

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          console.log('✅ Stream completed');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          // Handle SSE comment lines (heartbeats)
          if (line.startsWith(':')) {
            continue;
          }

          if (line.trim()) {
            // Handle NDJSON chunks
            try {
              const data = JSON.parse(line);

              // First stream event: the real DB ids for this turn. Stamp them
              // onto the optimistic messages while they're still in thinking
              // state (no text on screen yet), so no id reconciliation is ever
              // needed once content is visible.
              if (data.type === 'stream_start') {
                const realUserId = data.user_message_id;
                const realAssistantId = data.assistant_message_id;
                // Capture the ids to match on BEFORE updating the trackers —
                // the setMessages updater runs later and must target the old ids.
                const prevUserId = currentUserId;
                const prevAssistantId = currentAssistantId;
                setMessages(prev => prev.map(msg => {
                  if (realUserId && msg.id === prevUserId) return { ...msg, id: realUserId };
                  if (realAssistantId && msg.id === prevAssistantId) return { ...msg, id: realAssistantId };
                  return msg;
                }));
                if (realUserId) currentUserId = realUserId;
                if (realAssistantId) currentAssistantId = realAssistantId;
                continue;
              }

              // Handle error type
              if (data.type === 'error') {
                console.error('❌ TAP Build Agent error:', data);
                const errorMessage = data.message || 'An error occurred. Please try again.';

                // Replace thinking message with error message
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? { ...msg, content: errorMessage }
                    : msg
                ));
                
                setIsLoading(false);
                break;
              }

              if (data.type === 'RESPONSE_TOKEN') {
                const token = data.message || '';
                assistantContent += token;

                if (!firstChunkReceived) {
                  firstChunkReceived = true;
                }

                // Update assistant message in place
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? { ...msg, content: assistantContent }
                    : msg
                ));
              } else if (data.type === 'ACTION') {
                console.log(`📊 ${data.type}:`, data.message);
                applyThinkingAction(currentAssistantId, data.message || '');
              } else if (data.type === 'TOOL_CALL') {
                // Logged only — TOOL_CALL must not drive the progress indicator
                console.log(`📊 ${data.type}:`, data.message);
              } else if (data.type === 'FINAL_RESPONSE' && data.is_complete) {
                console.log('✅ TAP Build Agent completed');

                // Update final message with complete content and metadata
                setMessages(prev => prev.map(msg =>
                  msg.id === currentAssistantId
                    ? {
                      ...msg,
                      // Keep the streamed content — replacing text the user is
                      // already reading causes a visible change. The payload is
                      // only a fallback when nothing was streamed.
                      content: assistantContent || data.message,
                      token_usage: data.utilization ? {
                        input_tokens: data.utilization.input_tokens,
                        output_tokens: data.utilization.output_tokens,
                        total_tokens: data.utilization.total_tokens,
                        costs: {
                          input_cost: data.utilization.input_cost,
                          output_cost: data.utilization.output_cost,
                          total_cost: data.utilization.input_cost + data.utilization.output_cost
                        }
                      } : undefined
                    }
                    : msg
                ));

                if (data.last_agent) {
                  const agentTypeMap: Record<string, string> = {
                    'DataFormAgent': 'forms',
                    'DataObjectAgent': 'data_model',
                  };
                  detectedAgentType = agentTypeMap[data.last_agent] || null;
                  
                  // Extract business_key if available for forms
                  if (data.last_agent === 'DataFormAgent' && data.data_form?.business_key) {
                    console.log('📋 [TapBuildChat] Form business_key detected:', data.data_form.business_key);
                    // Store business_key to pass to parent
                    detectedAgentType = `forms:${data.data_form.business_key}`;
                  }
                }

                // Refresh project cost
                if (projectId && onCostUpdate) {
                  api.get(`/projects/${projectId}/stats`).then(response => {
                    const totalCost = parseFloat(response.data.total_cost) || 0;
                    onCostUpdate(totalCost);
                  }).catch(err => {
                    console.error('Failed to fetch project cost:', err);
                  });
                }
              }
            } catch (e) {
              // Not JSON, skip
            }
          }
        }
      }

    } catch (error: any) {
      console.error('Failed to send TAP build agent message:', error);

      if (error.name !== 'AbortError') {
        // Replace thinking message with error message
        setMessages(prev => prev.map(msg =>
          msg.id === currentAssistantId
            ? {
              ...msg,
              content: error.message || 'Failed to process your request. Please try again.',
            }
            : msg
        ));
      } else {
        // User aborted - remove the thinking message
        setMessages(prev => prev.filter(msg => msg.id !== currentAssistantId));
      }
    } finally {
      setIsLoading(false);
      setAbortController(null);

      // After streaming is complete and loading is done, trigger panel opening
      if (detectedAgentType && onAgentTypeDetected) {
        onAgentTypeDetected(detectedAgentType);
      }
    }
  };

  if (loadingHistory) {
    return null;
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-bg-primary">
      {/* Messages Area Wrapper */}
      <div className="flex-1 min-h-0 relative">
      <div
        ref={chatContainerRef}
        onScroll={(e) => { handleScroll(); chatWindow.onScroll(e); }}
        onWheel={chatWindow.onWheel}
        onPointerDown={chatWindow.armUserScroll}
        onTouchStart={chatWindow.armUserScroll}
        className="h-full flex flex-col items-center py-bg-v2-md overflow-y-auto modern-scrollbar-spaced"
      >
        <div className="flex flex-col gap-bg-v2-xl w-full max-w-[800px] relative px-4">
          {/* Messages */}
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1;
            const isStreamingMessage = isLastMessage && isLoading && message.role === 'assistant';

            return (
              <div
                key={message.id}
                id={`message-${message.id}`}
                className="animate-fade-in-up-smooth opacity-0"
                style={{
                  // Stagger only messages from the initial history load. Anything
                  // added later (sent, streamed, id-stamped) animates immediately —
                  // an index-based delay would leave remounted rows at opacity 0.
                  animationDelay: `${(message.staggerIndex ?? 0) * 0.05}s`,
                  animationFillMode: 'forwards'
                }}
              >
                <ChatMessage
                  message={{
                   ...message,
                    timestamp: formatTimestamp(new Date(message.created_at * 1000), {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: true
                    }) as any,
                    tokenUsage: mapTapAgentTokenUsage(message),
                    developmentTiming: mapTapAgentTiming(message),
                    isThinking: isStreamingMessage && !message.content,
                    thinkingMessage: isStreamingMessage && !message.content ? (message.thinkingMessage || 'Thinking...') : undefined,
                    isStreaming: isStreamingMessage && message.content.length > 0,
                  }}
                  currentUser={user}
                  project={null}
                  isDesignMode={false}
                  isFullWidthMessages={false}
                  onTicketTagClick={onTicketTagClick}
                  onEpicTagClick={onEpicTagClick}
                />
              </div>
            );
          })}

          <div ref={messagesEndRef} />
        </div>

      </div>

        {/* Pagination indicators - overlaid siblings of the scroll container */}
        {isLoadingMore && <ChatLoadingIndicator edge='top' />}
        {isLoadingNewer && <ChatLoadingIndicator edge='bottom' />}

        {/* Scroll to Bottom Button */}
        {(!isAtBottom || hasMoreNewer) && (
          <div className="absolute bottom-bg-v2-sm left-1/2 -translate-x-1/2 z-10 pointer-events-none">
            <Button
              onClick={async () => {
                setHasUserScrolledUp(false);
                // A historical window's loaded bottom is not the true tail —
                // reload the latest page first, then jump to it.
                if (hasMoreNewer) {
                  await fetchMessageHistory();
                  requestAnimationFrame(() =>
                    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                  );
                } else {
                  messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              variant="ghost"
              className="!w-9 !h-9 !rounded-bg-v2-full !bg-bg-primary border border-border-secondary !shadow-bg-v2-md hover:!bg-bg-secondary pointer-events-auto !p-bg-v2-none"
              title="Scroll to bottom"
              label={<Icon name={IconName.ArrowDown} size="sm" variant="secondary" />}
            />
          </div>
        )}

        {/* Conversation Navigator - right-edge minimap of user turns */}
        <ChatNavigator
          items={navItems}
          onSelect={chatWindow.jumpTo}
          containerRef={chatContainerRef}
          getMessageElement={getMessageElement}
          loadedCount={windowMessages.length}
          oldestLoadedId={windowMessages[0]?.id}
          newestLoadedId={windowMessages[windowMessages.length - 1]?.id}
        />
      </div>

      {/* Queue panel + chat input */}
      <div className="flex flex-col items-center gap-bg-v2-sm pt-bg-v2-lg pb-bg-v2-3xl px-bg-v2-xl bg-bg-primary">
          {/* Queue panel + input share one centered column so the panel docks
              flush onto the top edge of the input box. */}
          <div className="w-full max-w-[800px] flex flex-col items-stretch">
            {/* Outside the permission gate on purpose: a viewer can see what
                is queued and that the queue is paused, they just cannot manage
                the cards — canEditProject handles that per card. */}
            <QueuedMessageBanner
              jobs={promptQueue.queuedJobs}
              onEdit={handleEditJob}
              onResumeAll={promptQueue.resumeAll}
              onCancel={(jobId) => {
                if (editingQueueJobId === jobId) {
                  setInput('');
                  setEditingQueueJobId(null);
                }
                promptQueue.cancelJob(jobId);
              }}
              editingJobId={editingQueueJobId}
              canEditProject={canEditProject}
              currentUserId={user?.id}
            />
            <IfHasPermission
              permissions={project?.permissions}
              requires={Permissions.UpdateProjectPermission}
              fallback={<ReadOnlyProjectNotice className="mx-bg-v2-xl mb-bg-v2-3xl" />}
            >
            <ChatInput
              className={promptQueue.queuedJobs.length > 0 ? '[&>div]:!rounded-t-none [&>div]:!border-t-0' : ''}
              value={input}
              onChange={setInput}
              onSend={handleSendMessage}
              onStop={handleStopStreaming}
              placeholder={!isLinked ? "Please link your TAP site to enable the Build Agent" : "Ask about forms, workflows, administration, reports, or queues — or @-tag tickets and epics to implement them"}
              // Only the send action is gated while a prompt runs — the
              // textarea stays live so the next prompt can be queued.
              disabled={!isLinked}
              isStreaming={isLoading}
              isSending={isLoading}
              projectId={projectId || undefined}
              showAttachmentButton={true}
              requireTextToSend={true}
              allowedDocumentExtensions={[...DOCUMENT_EXTENSIONS]}
              enableTagChips
              onTagSearch={handleTicketTagSearch}
              onTagsChange={handleTicketTagsChange}
              tagSuggestions={ticketTagSuggestions}
              isTagSearching={isTicketTagSearching}
              isQueueMode={isLoading}
              onQueue={handleQueueMessage}
              editingJobId={editingQueueJobId}
              editingAttachments={promptQueue.editingJob?.attachments}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
            />
            </IfHasPermission>
          </div>
        </div>
    </div>
  );
};